import axios, { AxiosResponse } from 'axios';
import { ApiResponse, GetReportResponse, zps_ea_war, zps_wp } from '../zps';
import { handleDownload } from '../../app/utils/script';

export type IAPMTransaction = {
  key: APMTransKey;
  gid: number;
  entid: number;
  sid: number;
  amount: number;
  bankid: number;
  cid: number;
  dateTime: string;
};

export type IAPMPeriods = {
  key: APMTransKey;
  gid: number;
  entid: number;
  sid: number;
  amount: number;
  amountCommitions: number;
  amountWithoutCommitions: number;
  bankid: number;
  cid: number;
  dateTime: string;
  totalTransactions: number;
};

export type IAPMPComissions = {
  key: APMTransKey;
  a052: number;
  a054: string;
  amount: number;
  amountCommission: number;
  cid: number;
  pCommission: string;
};

export type APMTransKey = {
  a001: number;
  a029: number;
  a031: number;
  a032: number;
  dateTime: string;
  movType: string;
};

export interface IAPMTransactionResponse extends ApiResponse {
  transactions: IAPMTransaction[];
  payment: boolean;
  totalRows: number;
  type: string;
}

export interface IAPMPeriodsResponse extends ApiResponse {
  commissions: IAPMPComissions[];
  periods: IAPMPeriods[];
  payment: boolean;
  totalRows: number;
  type: string;
}

export type IAPMPos = {
  description: string;
  entid: number;
  posid: number;
  sid: number;
};

export interface fetchApmPOSEquipmentListResponse extends ApiResponse {
  poss: IAPMPos[];
}

export type apmEquipPos = {
  account: number;
  active: boolean;
  bankid: number;
  description: string;
  eid?: number;
  entid: number;
  gid: number;
  inuse: number;
  mbway: boolean;
  merchantid: number;
  posid: number;
  poskey?: string;
  sid: number;
  timeopenclose?: number;
  zsversion: number;
};

export type IAPMPosEquipment = {
  eid?: number;
  lastComDt?: string;
  lastOperation?: string;
  lastOperationDt?: string;
  status?: number;
  pos: apmEquipPos;
};

export interface fetchPOSEquipmentDetailsResponse extends ApiResponse {
  poss: IAPMPosEquipment[];
  totalRows: number;
}

export interface ReceiptResponse extends ApiResponse {
  receipt: string;
}

export const fetchAPMTransactionList = (
  controller: AbortController,
  entid: number,
  sid: number,
  posid: number,
  offset: number,
  limit: number,
  totalRows: boolean,
  begin?: string,
  end?: string
): Promise<AxiosResponse<IAPMTransactionResponse>> => {
  const url =
    `${zps_wp}/api/apm/transaction?entid=${entid}&sid=${sid}&posid=${posid}&offset=${offset}&limit=${limit}&totalRows=${totalRows}` +
    (begin ? `&begin=${begin}` : '') +
    (end ? `&end=${end}` : '');

  return axios.get<IAPMTransactionResponse>(url, { signal: controller.signal });
};

export const fetchApmPOSEquipmentList = (
  controller: AbortController,
  entid: number,
  sid: number
): Promise<AxiosResponse<fetchApmPOSEquipmentListResponse>> => {
  return axios.get<fetchApmPOSEquipmentListResponse>(`${zps_wp}/api/apm/pos/label?entid=${entid}&sid=${sid}`, {
    signal: controller.signal
  });
};

export const fetchAPMPeriodsList = (
  controller: AbortController,
  entid: number,
  sid: number,
  posid: number,
  offset: number,
  limit: number,
  totalRows: boolean,
  begin?: string,
  end?: string
): Promise<AxiosResponse<IAPMPeriodsResponse>> => {
  const url =
    `${zps_wp}/api/apm/period?entid=${entid}&sid=${sid}&posid=${posid}&offset=${offset}&limit=${limit}&totalRows=${totalRows}` +
    (begin ? `&begin=${begin}` : '') +
    (end ? `&end=${end}` : '');

  return axios.get<IAPMPeriodsResponse>(url, { signal: controller.signal });
};

export const fetchReceiptData = (
  a001: number,
  movType: string,
  a031: number,
  a032: number,
  a029: number,
  dateTime: string,
  controller: AbortController
): Promise<AxiosResponse<ReceiptResponse>> => {
  const url = `${zps_wp}/api/apm/receipt?a001=${a001}&movType=${movType}&a031=${a031}&a032=${a032}&a029=${a029}&dateTime=${new Date(
    dateTime
  ).toISOString()}`;

  return axios.get<ReceiptResponse>(url, { signal: controller.signal });
};

export const fetchComissionsData = (
  a001: number,
  movType: string,
  a031: number,
  a032: number,
  a029: number,
  dateTime: string,
  controller: AbortController
): Promise<AxiosResponse<IAPMPeriodsResponse>> => {
  const url = `${zps_wp}/api/apm/period/commission?a001=${a001}&movType=${movType}&a031=${a031}&a032=${a032}&a029=${a029}&dateTime=${new Date(
    dateTime
  ).toISOString()}`;

  return axios.get<IAPMPeriodsResponse>(url, { signal: controller.signal });
};

export const fetchTransactionData = (
  a001: number,
  movType: string,
  a031: number,
  a032: number,
  a029: number,
  dateTime: string,
  controller: AbortController
): Promise<AxiosResponse<IAPMTransactionResponse>> => {
  const url = `${zps_wp}/api/apm/period/transaction?a001=${a001}&movType=${movType}&a031=${a031}&a032=${a032}&a029=${a029}&dateTime=${new Date(
    dateTime
  ).toISOString()}`;

  return axios.get<IAPMTransactionResponse>(url, {
    signal: controller.signal
  });
};

export const fetchPOSEquipmentDetails = (
  controller: AbortController,
  entid: number,
  sid: number,
  posid: number,
  offset: number,
  limit: number,
  totalRows: boolean
): Promise<AxiosResponse<fetchPOSEquipmentDetailsResponse>> => {
  const url = `${zps_wp}/api/apm/pos/details?entid=${entid}&sid=${sid}&offset=${offset}&posid=${posid}&limit=${limit}&totalRows=${totalRows}`;

  return axios.get<fetchPOSEquipmentDetailsResponse>(url, { signal: controller.signal });
};

export const updateApmEquipmentDetails = (
  posid: number,
  timeopenclose: number,
  description: string,
  controller: AbortController
): Promise<AxiosResponse<ApiResponse>> => {
  const url = `${zps_wp}/api/apm/pos`;

  return axios.put<ApiResponse>(url, { posid, timeopenclose, description }, { signal: controller.signal });
};

export const fetchAPMTransactionsXLS = async (
  controller: AbortController,
  entid: number,
  sid: number,
  offset: number,
  limit: number,
  begin?: string | null,
  end?: string
): Promise<AxiosResponse<GetReportResponse>> => {
  try {
    const res = await axios.get<GetReportResponse>(
      `${zps_ea_war}/apm/transaction/report?format=xls&entid=${entid}&sid=${sid}&offset=${offset}&limit=${limit}` +
        `${begin ? `&begin=${begin}` : ''}` +
        `${end ? `&end=${end}` : ''}`,
      { signal: controller.signal }
    );
    if (res.data?.report) {
      handleDownload(res.data.report, res.data.name, res.data.mime);
    }
    return res;
  } catch (error) {
    throw error; // IMPORTANT: lets handleApiError catch it
  }
};

export const fetchAPMPeriodsXLS = async (
  controller: AbortController,
  entid: number,
  sid: number,
  offset: number,
  limit: number,
  begin?: string | null,
  end?: string
): Promise<AxiosResponse<GetReportResponse>> => {
  try {
    const res = await axios.get<GetReportResponse>(
      `${zps_ea_war}/apm/period/report?format=xls&entid=${entid}&sid=${sid}&offset=${offset}&limit=${limit}` +
        `${begin ? `&begin=${begin}` : ''}` +
        `${end ? `&end=${end}` : ''}`,
      { signal: controller.signal }
    );
    if (res.data?.report) {
      handleDownload(res.data.report, res.data.name, res.data.mime);
    }

    return res;
  } catch (error) {
    throw error; // IMPORTANT: lets handleApiError catch it
  }
};

export const fetchAPMPeriodsPDF = async (
  controller: AbortController,
  entid: number,
  sid: number,
  offset: number,
  limit: number,
  begin?: string | null,
  end?: string
): Promise<AxiosResponse<GetReportResponse>> => {
  try {
    const res = await axios.get<GetReportResponse>(
      `${zps_ea_war}/apm/period/report?format=pdf&entid=${entid}&sid=${sid}&offset=${offset}&limit=${limit}` +
        `${begin ? `&begin=${begin}` : ''}` +
        `${end ? `&end=${end}` : ''}`,
      { signal: controller.signal }
    );
    if (res.data?.statusOper?.code === 0 && res.data.report) {
      handleDownload(res.data.report, res.data.name, res.data.mime);
    }
    return res;
  } catch (error) {
    throw error; // IMPORTANT: lets handleApiError catch it
  }
};
